// MACHINERY V2 (Stroje) — "Pridať z katalógu", the reduced add form (M2.8).
//
// Spec: data package §11 prefill table. Reference impl:
// design_handoff_machinery_v3/machinery-catalog.jsx (`AddDialog`); artboard
// 0903. Logic half = useCatalogAddForm.ts; FORM BODY = catalogAddFields.tsx;
// the below-1280 shell = CatalogAddSheet.tsx (all three, 300-line cap).
//
// >>> SPLIT AT M2.16c, THEN BRANCHED. <<< This file stood at 288 of 300 lines
// and the mobile pass had to give the same form a SECOND shell (board 18,
// mobile/screenshots/18-katalog-add-sheet.png). The near-cap rule made the split
// the first task: the form body moved out whole, and what remains is the SHELL
// CHOICE plus the desktop dialog's own chrome and footer.
//
// >>> ONE FORM STATE ACROSS BOTH SHELLS. <<< `useCatalogAddForm` is called HERE,
// once, and handed to whichever shell renders — so rotating a tablet across 1280
// mid-entry keeps the typed serial, the year, the location and any submit error.
// If the hook were called inside each shell, that rotation would remount the
// form and silently discard exactly the work rule (d) protects.
//
// >>> THREE EDITABLE FIELDS. EVERYTHING ELSE IS SERVER-RESOLVED. <<<
// serialNumber (required, max 50) · productionYear (4 digits, 1900..now) ·
// locationId. The validation rules are lifted field-for-field from M2.7's
// `CustomMachineTail.tsx:43-75`, which lifted them from v1 — the same three
// rules, so a serial that is valid on one add path is valid on both.
//
// >>> RULE (d) / §9 D7: THE FORM STAYS OPEN UNTIL THE SERVER SAYS YES. <<<
// `onCreated` is passed straight into the hook and is called ONLY inside the
// success branch, after `await dispatch(createMachine(...)).unwrap()`. A failure
// sets `submitError`, touches no field, and renders a `RetryNotice` whose
// onRetry re-submits the SAME values. `resetAll()` runs only on the deliberate
// LEAVE paths below — Cancel, ✕, Esc, backdrop — never on failure. True in BOTH
// shells, because both close through this file's `leave`.
//
// The desktop shell is `TabDialog` (radius 12, glyph, `subtitle` back-link slot)
// — the v2 dialog shell, not a second one. `onBack` fills the subtitle slot only
// when the caller has somewhere to go back TO: the fleet add flow does (the
// picker), the catalog pages do not.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { V2_BELOW_DESKTOP, useV2MediaQuery } from "../useV2MediaQuery";
import { CatalogModelListItem } from "../../models/models";
import { BackLink } from "../add/addAtoms";
import { ListGlyph } from "../forms/FormGlyphs";
import TabDialog from "../profile/tabs/TabDialog";
import CatalogAddFields from "./catalogAddFields";
import CatalogAddSheet from "./CatalogAddSheet";
import useCatalogAddForm from "./useCatalogAddForm";

/** The footer button submits the body's form across the DOM boundary the shell
 *  puts between them — the same plain `form=` association M2.7 uses, and it
 *  works identically inside TabDialog and inside SheetV2. */
const FORM_ID = "mv2-catalog-add-form";

export interface CatalogAddDialogV2Props {
  open: boolean;
  model: CatalogModelListItem;
  onClose: () => void;
  /** Fired ONLY after createMachine resolves — the sole close-on-success path. */
  onCreated: (id?: string) => void;
  /** "Späť na možnosti" / "Zmeniť stroj" — omitted when there is no way back. */
  onBack?: () => void;
}

const CatalogAddDialogV2: React.FC<CatalogAddDialogV2Props> = ({
  open,
  model,
  onClose,
  onCreated,
  onBack,
}) => {
  const { translate } = useTranslation(["myMachinery", "common"]);
  // F-A: the module's ONE below-desktop predicate — the same string the v2
  // stylesheets fence on. See useV2MediaQuery.ts.
  const isDesktop = !useV2MediaQuery(V2_BELOW_DESKTOP);
  const f = useCatalogAddForm(open, model, onCreated);

  const leave = (next: () => void) => {
    f.resetAll();
    next();
  };

  if (!isDesktop) {
    return (
      <CatalogAddSheet
        open={open}
        formId={FORM_ID}
        model={model}
        f={f}
        onLeave={leave}
        onClose={onClose}
        onBack={onBack}
      />
    );
  }

  return (
    <TabDialog
      open={open}
      onOpenChange={(o) => {
        if (!o) leave(onClose);
      }}
      width={600}
      title={translate("myMachinery:v2.catalog.addFromCatalog")}
      glyph={<ListGlyph />}
      subtitle={
        onBack ? (
          <BackLink
            label={translate("myMachinery:v2.add.backToOptions")}
            onClick={() => leave(onBack)}
          />
        ) : undefined
      }
      footerNote={translate("myMachinery:v2.add.requiredNote")}
      footer={
        <React.Fragment>
          <button type="button" className="btn btn-ghost" onClick={() => leave(onClose)}>
            {translate("common:buttons.cancel")}
          </button>
          <button type="submit" form={FORM_ID} className="btn btn-mod" disabled={f.submitting}>
            <span aria-hidden style={{ fontSize: 15, lineHeight: 1, marginRight: 2 }}>
              +
            </span>
            {translate("myMachinery:v2.add.submit")}
          </button>
        </React.Fragment>
      }
    >
      <CatalogAddFields
        formId={FORM_ID}
        model={model}
        f={f}
        onBack={onBack ? () => leave(onBack) : undefined}
      />
    </TabDialog>
  );
};

export default CatalogAddDialogV2;
